import { useState } from 'react';
import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Filters,
  InlineStack,
  Modal,
  ResourceItem,
  ResourceList,
  Spinner,
  Text,
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { SchedulesEmptyState } from '../shared/EmptyStates';
import { formatTime } from '../shared/useNotificationFormState';
import type { NotificationSchedule } from '@/queries/notifications';

export interface SchedulesTabProps {
  schedules: NotificationSchedule[];
  isLoading: boolean;
  onToggle: (id: number, enabled: boolean) => void;
  onDelete: (id: number) => void;
  togglePending: boolean;
  deletePending: boolean;
  onGotoCompose: () => void;
}

export function SchedulesTab({
  schedules,
  isLoading,
  onToggle,
  onDelete,
  togglePending,
  deletePending,
  onGotoCompose,
}: SchedulesTabProps) {
  const [filterStatus, setFilterStatus] = useState<'all' | 'enabled' | 'disabled'>('all');
  const [query, setQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<NotificationSchedule | null>(null);

  if (isLoading) {
    return (
      <Card>
        <InlineStack align="center"><Spinner accessibilityLabel="loading" /></InlineStack>
      </Card>
    );
  }

  if (!schedules.length) {
    return (
      <Card>
        <SchedulesEmptyState onGotoCompose={onGotoCompose} />
      </Card>
    );
  }

  const filtered = schedules.filter((s) => {
    if (filterStatus === 'enabled' && !s.enabled) return false;
    if (filterStatus === 'disabled' && s.enabled) return false;
    if (query && !s.title.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      <Card padding="0">
        <ResourceList
          resourceName={{ singular: 'schedule', plural: 'schedules' }}
          items={filtered}
          filterControl={
            <Filters
              queryValue={query}
              queryPlaceholder="ابحث في الـ schedules…"
              onQueryChange={setQuery}
              onQueryClear={() => setQuery('')}
              filters={[
                {
                  key: 'status',
                  label: 'الحالة',
                  filter: (
                    <BlockStack gap="100">
                      <Button
                        pressed={filterStatus === 'all'}
                        onClick={() => setFilterStatus('all')}
                        variant="plain"
                      >
                        الكل
                      </Button>
                      <Button
                        pressed={filterStatus === 'enabled'}
                        onClick={() => setFilterStatus('enabled')}
                        variant="plain"
                      >
                        شغّال
                      </Button>
                      <Button
                        pressed={filterStatus === 'disabled'}
                        onClick={() => setFilterStatus('disabled')}
                        variant="plain"
                      >
                        موقّف
                      </Button>
                    </BlockStack>
                  ),
                  shortcut: true,
                },
              ]}
              onClearAll={() => {
                setQuery('');
                setFilterStatus('all');
              }}
            />
          }
          renderItem={(s) => (
            <ScheduleItem
              schedule={s}
              onToggle={() => onToggle(s.id, !s.enabled)}
              onDelete={() => setConfirmDelete(s)}
              togglePending={togglePending}
              deletePending={deletePending}
            />
          )}
        />
      </Card>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="تأكيد الحذف"
        primaryAction={{
          content: 'حذف نهائيًا',
          destructive: true,
          loading: deletePending,
          onAction: () => {
            if (confirmDelete) {
              onDelete(confirmDelete.id);
              setConfirmDelete(null);
            }
          },
        }}
        secondaryActions={[{ content: 'إلغاء', onAction: () => setConfirmDelete(null) }]}
      >
        <Modal.Section>
          <Text as="p">
            هتـ delete schedule بعنوان: <strong>{confirmDelete?.title}</strong>
            <br />
            مش هترجع تاني بعد كده.
          </Text>
        </Modal.Section>
      </Modal>
    </>
  );
}

interface ScheduleItemProps {
  schedule: NotificationSchedule;
  onToggle: () => void;
  onDelete: () => void;
  togglePending: boolean;
  deletePending: boolean;
}

function ScheduleItem({
  schedule: s,
  onToggle,
  onDelete,
  togglePending,
  deletePending,
}: ScheduleItemProps) {
  const meta =
    s.schedule_type === 'one_time' && s.fire_at
      ? `يطلق: ${formatTime(s.fire_at)}`
      : s.schedule_type === 'recurring'
      ? `cron: ${s.cron_expr}`
      : s.schedule_type === 'trigger'
      ? `trigger: ${s.trigger_kind}`
      : '';

  return (
    <ResourceItem
      id={String(s.id)}
      onClick={() => {}}
      accessibilityLabel={s.title}
    >
      <InlineStack align="space-between" blockAlign="center" wrap={false}>
        <Box minWidth="0" width="100%">
          <BlockStack gap="050">
            <InlineStack gap="200" blockAlign="center">
              <Text as="h3" variant="headingSm">
                {s.title}
              </Text>
              <Badge tone={s.enabled ? 'success' : undefined}>
                {s.enabled ? 'شغّال' : 'موقّف'}
              </Badge>
              <Badge tone="info">{s.schedule_type}</Badge>
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued">
              {meta}
              {' · '}
              أُطلق {s.fire_count} مرة
              {s.last_fired_at ? ` · آخر مرة ${formatTime(s.last_fired_at)}` : ''}
            </Text>
          </BlockStack>
        </Box>
        <InlineStack gap="100">
          <Button onClick={onToggle} loading={togglePending}>
            {s.enabled ? 'إيقاف' : 'تشغيل'}
          </Button>
          <Button icon={DeleteIcon} variant="tertiary" tone="critical" onClick={onDelete} loading={deletePending}>
            حذف
          </Button>
        </InlineStack>
      </InlineStack>
    </ResourceItem>
  );
}
